/**
 * Process `FolderCoverageProvider`.
 *
 * Once a folder has canonical process records (written via
 * FolderRecordService.upsertMany at publish time — see
 * ProcessDocFolderRecordMaterializerService), grade five layers per the
 * plan in docs/plans/2026-05-08-compass-source-review-and-folder-fidelity-plan.md
 * §Design 2:
 *
 *   1. Procedure  — process records that exist (has name + description)
 *   2. Citations  — records carrying ≥1 universal-provenance citation
 *   3. Controls   — records with a non-empty `controls[]` array
 *   4. Evidence   — records with a non-empty `evidence[]` array
 *   5. Automation — records with `automationStatus` or `automationTool`
 *
 * Before any records exist (pre-publish), fall back to the legacy
 * sources / extraction layers so the HUD still reflects progress.
 *
 * The overall score is the mean of the layer ratios.
 *
 * See `docs/architecture/sources-as-folder-artifacts.md`.
 */

import type {
  CoverageLayer,
  FolderCoverageInput,
  FolderCoverageProvider,
  FolderCoverageReport,
} from '../../../../core/folderCoverage/types';
import type { KnowledgeRun } from '../../../compass/models/KnowledgeIngestion';
import type { FolderRecord } from '../../../data-binding/models/FolderRecord';

const PROCESS_SOURCE_KINDS = new Set([
  'process-doc-r2',
  'process-doc-url',
  'process-repository',
  'wiki',
  'spreadsheet',
]);

const PROCESS_NODE_TYPES = new Set([
  'process',
  'flow',
  'task',
  'activity',
  'pool',
  'lane',
  'start_event',
  'end_event',
  'gateway',
]);

function latestRunForSource(
  runs: readonly KnowledgeRun[],
  externalJobId: string | null,
): KnowledgeRun | null {
  if (!externalJobId) return null;
  return runs.find((r) => r.externalJobId === externalJobId) ?? null;
}

function recordHasNonEmptyArray(
  props: Record<string, unknown>,
  key: string,
): boolean {
  const v = props[key];
  return Array.isArray(v) && v.length > 0;
}

function recordHasCitations(props: Record<string, unknown>): boolean {
  const provenance = props.provenance as { citations?: unknown[] } | undefined;
  return Array.isArray(provenance?.citations) && provenance.citations.length > 0;
}

function recordHasAutomation(props: Record<string, unknown>): boolean {
  const status = typeof props.automationStatus === 'string' ? props.automationStatus.trim() : '';
  const tool = typeof props.automationTool === 'string' ? props.automationTool.trim() : '';
  return status.length > 0 || tool.length > 0;
}

function recordHasProcedure(props: Record<string, unknown>): boolean {
  const name = typeof props.name === 'string' ? props.name.trim() : '';
  return name.length > 0;
}

function computeRecordBackedLayers(
  records: readonly FolderRecord[],
): { layers: CoverageLayer[]; total: number } {
  const procedureRecords = records.filter(isCanonicalProcedureRecord);
  const total = procedureRecords.length;
  if (total === 0) return { layers: [], total: 0 };

  let procedure = 0;
  let citations = 0;
  let controls = 0;
  let evidence = 0;
  let automation = 0;
  for (const r of procedureRecords) {
    const p = r.universalProps;
    if (recordHasProcedure(p)) procedure += 1;
    if (recordHasCitations(p)) citations += 1;
    if (recordHasNonEmptyArray(p, 'controls')) controls += 1;
    if (recordHasNonEmptyArray(p, 'evidence')) evidence += 1;
    if (recordHasAutomation(p)) automation += 1;
  }

  const layers: CoverageLayer[] = [
    {
      id: 'procedure',
      label: 'Procedure',
      description: 'Canonical processes captured with a procedural description.',
      ratio: procedure / total,
      filled: procedure,
      total,
    },
    {
      id: 'citations',
      label: 'Citations',
      description:
        'Processes with provenance citations back to source documents — the trust signal that powers the source-process review viewer.',
      ratio: citations / total,
      filled: citations,
      total,
      upsell:
        citations === 0
          ? {
              connectorId: 'sop-document',
              headline: 'No citations yet',
              blurb:
                'Re-run extraction with snippet capture enabled, or hand-cite during review, so reviewers can verify each process against its source paragraph.',
            }
          : undefined,
    },
    {
      id: 'controls',
      label: 'Controls',
      description:
        'Processes mapped to framework controls (ACQS, SOC 2, ISO 27001, NDIS, HIPAA). Drives the compliance lens.',
      ratio: controls / total,
      filled: controls,
      total,
      upsell:
        controls === 0
          ? {
              connectorId: 'grc-register',
              headline: 'No controls mapped',
              blurb:
                'Connect a GRC register or run framework suggestions to start mapping controls onto every process.',
            }
          : undefined,
    },
    {
      id: 'evidence',
      label: 'Evidence',
      description:
        'Processes with linked evidence artifacts. Required for audit-pack export.',
      ratio: evidence / total,
      filled: evidence,
      total,
    },
    {
      id: 'automation',
      label: 'Automation',
      description:
        'Processes carrying automation status or tooling — surface for automation-candidate triage.',
      ratio: automation / total,
      filled: automation,
      total,
    },
  ];

  return { layers, total };
}

function isCanonicalProcedureRecord(record: FolderRecord): boolean {
  const props = record.universalProps;
  if (readString(props.parentProcessId) || readString(props.parentId)) return false;
  if (readString(props.nonGraphReason)) return false;

  const type = readString(props.type) ?? readString(props.claimType);
  const claimType = readString(props.claimType);
  if (type === 'process' || type === 'flow') return true;
  if (claimType === 'process') return true;
  if (type === 'activity' && props.isAggregate === true && readString(props.processKind)) {
    return true;
  }

  const processId = readString(props.processId);
  return Boolean(processId && (!record.externalId || record.externalId === processId));
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function computeProcessFolderCoverage(input: FolderCoverageInput): FolderCoverageReport {
  const folderRecords = input.folderRecords ?? [];
  const recordBacked = computeRecordBackedLayers(folderRecords);

  const processSources = input.sources.filter((s) => PROCESS_SOURCE_KINDS.has(s.kind));
  const sourceCount = processSources.length;

  const runs = input.runs ?? [];
  const extractedCount = processSources.filter((s) => {
    const run = latestRunForSource(runs, s.latestJobId);
    return run?.status === 'completed';
  }).length;

  const processNodes = input.nodes.filter((n) => PROCESS_NODE_TYPES.has(String(n.type)));
  const hasAnyProcess = processNodes.length > 0;

  const layers: CoverageLayer[] = [
    {
      id: 'sources',
      label: 'Sources',
      description:
        'Documents, URLs, wikis, and repositories feeding this folder. Each one becomes a capture run when it updates.',
      ratio: sourceCount > 0 ? 1 : 0,
      filled: sourceCount,
      total: sourceCount > 0 ? sourceCount : 1,
      upsell:
        sourceCount === 0
          ? {
              connectorId: 'sop-document',
              headline: 'No process sources yet',
              blurb:
                'Upload an SOP PDF, Google Doc, Confluence page, or point at a wiki to seed this folder. Each source becomes a capture run that extracts process structure.',
            }
          : undefined,
    },
    {
      id: 'extraction',
      label: 'Extraction',
      description:
        'Sources whose most recent capture run completed successfully — the fraction of uploaded material actually turned into process structure.',
      ratio: sourceCount > 0 ? extractedCount / sourceCount : 0,
      filled: extractedCount,
      total: sourceCount,
      upsell:
        sourceCount > 0 && extractedCount === 0
          ? {
              connectorId: 'sop-document',
              headline: 'No extractions yet',
              blurb:
                'Every uploaded source needs at least one capture run. Kick one off from the source card to turn the document into a process diagram.',
            }
          : undefined,
    },
    {
      id: 'processes',
      label: 'Captured processes',
      description:
        'Process / flow / task nodes in the folder graph. Drives the navigable process map.',
      ratio: hasAnyProcess ? 1 : 0,
      filled: processNodes.length,
      total: hasAnyProcess ? processNodes.length : 1,
      upsell:
        !hasAnyProcess
          ? {
              connectorId: 'sop-document',
              headline: 'No processes captured',
              blurb:
                'Once a capture run completes, this folder will contain a process map. Add a source to get started.',
            }
          : undefined,
    },
  ];

  // Prefer the record-backed layers once any canonical processes have
  // been published into folder_records. Until then fall back to the
  // legacy source / extraction / processes layers so the HUD still
  // reflects in-flight work before the first publish.
  const useRecordBacked = recordBacked.total > 0;
  const finalLayers = useRecordBacked ? recordBacked.layers : layers;
  const overall = finalLayers.reduce((a, l) => a + l.ratio, 0) / Math.max(finalLayers.length, 1);
  const resourceCount = useRecordBacked ? recordBacked.total : processNodes.length;

  return {
    domain: 'process',
    layers: finalLayers,
    overall,
    resourceCount,
    sourceSummary: {
      hasProcessSource: sourceCount > 0,
      hasCompletedExtraction: extractedCount > 0,
      hasCapturedProcess: hasAnyProcess || useRecordBacked,
    },
  };
}

export const processFolderCoverageProvider: FolderCoverageProvider = {
  domain: 'process',
  compute: computeProcessFolderCoverage,
};
